import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeAccionProductos } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

/** Igual que la pestaña "Cuentas Contables" en MantenimientoProductos. */
const PROGRAMA_CUENTAS = 'cuentascontables';

/** Mismos estilos de campo que `Motores/informemotores.js` (INPUT_SX). */
const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

/** Botones primarios/outline alineados con `Motores/informemotores.js`. */
const outlineBtnSx = {
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const primaryBtnSx = {
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  boxShadow: 'none',
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const AsociacionCuentasContables = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;

  const tablaInvStr = useMemo(
    () => (selectedCompany?.anomesfacturacion ? String(selectedCompany.anomesfacturacion).toLowerCase() : ''),
    [selectedCompany?.anomesfacturacion]
  );

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_CUENTAS);
  const puedeModificar = puedeAccionProductos(roleDetalles, PROGRAMA_CUENTAS, 'modificar');
  const puedeEliminar = puedeAccionProductos(roleDetalles, PROGRAMA_CUENTAS, 'borrar');
  
  // Estados principales
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [productosBusqueda, setProductosBusqueda] = useState([]);
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [dialogoBusqueda, setDialogoBusqueda] = useState(false);
  
  // Estados para cuentas
  const [cuentasAsociadas, setCuentasAsociadas] = useState([]);
  const [codigoCuentaNueva, setCodigoCuentaNueva] = useState('');
  const [descripcionCuentaNueva, setDescripcionCuentaNueva] = useState('');
  const [tipoAsociacion, setTipoAsociacion] = useState('Inventario'); // Inventario, Gasto, Otra
  const [cargando, setCargando] = useState(false);
  const [dialogoAgregarCuenta, setDialogoAgregarCuenta] = useState(false);
  const [cuentasSugeridas, setCuentasSugeridas] = useState([]);
  const [busquedaCuenta, setBusquedaCuenta] = useState('');

  const realizarBusqueda = async (texto = busquedaTexto) => {
    if (!texto || texto.length < 2) {
      return;
    }
    if (!selectedCompany?.db_empresa || !tablaInvStr) {
      showError('Empresa o tabla de inventario no disponible');
      return;
    }

    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_buscarproductos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tablaInvStr,
          descripcion: (texto !== undefined && texto !== null && texto !== '') ? texto : busquedaTexto,
          local: `${selectedCompany?.almacenpordefecto ?? '0'}-`,
          usuario: usuarioLog,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setProductosBusqueda(data.productos || []);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al buscar');
      }
    } catch (error) {
      showError('Error de conexión al buscar');
    }
  };

  const seleccionarProducto = async (codigo) => {
    if (!selectedCompany?.db_empresa || !tablaInvStr) return;
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_obtenerproducto', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tablaInvStr,
          codigo,
          usuario: usuarioLog,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const producto = data.producto;
        setProductoSeleccionado(producto);
        setDialogoBusqueda(false);
        setBusquedaTexto('');
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al obtener producto');
      }
    } catch (error) {
      showError('Error de conexión al obtener producto');
    }
  };

  const cargarCuentasAsociadas = async () => {
    if (!productoSeleccionado || !tablaInvStr) return;

    setCargando(true);
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_obtenercuentasasociadas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tablaInvStr,
          producto_id: productoSeleccionado.id,
          usuario: usuarioLog,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCuentasAsociadas(data.cuentas || []);
      } else {
        const errorData = await response.json();
        // Si el endpoint no existe aún, usar las cuentas del producto
        if (errorData.Error && errorData.Error.includes('no encontrado')) {
          // Cargar cuentas desde el producto directamente
          const cuentas = [];
          if (productoSeleccionado.cuentainvent) {
            cuentas.push({
              codigo: productoSeleccionado.cuentainvent,
              tipo: 'Inventario',
              descripcion: productoSeleccionado.cuentainvent,
            });
          }
          if (productoSeleccionado.cuentagasto) {
            cuentas.push({
              codigo: productoSeleccionado.cuentagasto,
              tipo: 'Gasto',
              descripcion: productoSeleccionado.cuentagasto,
            });
          }
          setCuentasAsociadas(cuentas);
        } else {
          showError(errorData.Error || 'Error al cargar cuentas asociadas');
        }
      }
    } catch (error) {
      // Si falla, usar las cuentas del producto directamente
      const cuentas = [];
      if (productoSeleccionado.cuentainvent) {
        cuentas.push({
          codigo: productoSeleccionado.cuentainvent,
          tipo: 'Inventario',
          descripcion: productoSeleccionado.cuentainvent,
        });
      }
      if (productoSeleccionado.cuentagasto) {
        cuentas.push({
          codigo: productoSeleccionado.cuentagasto,
          tipo: 'Gasto',
          descripcion: productoSeleccionado.cuentagasto,
        });
      }
      setCuentasAsociadas(cuentas);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!productoSeleccionado || !tablaInvStr) return;
    cargarCuentasAsociadas();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recarga al cambiar producto o período fiscal
  }, [productoSeleccionado, tablaInvStr]);

  const buscarCuentas = async (texto) => {
    if (!selectedCompany?.db_empresa) return;
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_buscarcuentas', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          busqueda: texto,
          usuario: usuarioLog,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCuentasSugeridas(data.cuentas || []);
      }
    } catch (error) {
      console.error('Error buscando cuentas', error);
    }
  };

  
  const handleAgregarCuenta = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar asociaciones de cuentas.');
      return;
    }
    if (!codigoCuentaNueva.trim()) {
      showError('Debe ingresar un código de cuenta');
      return;
    }

    if (!productoSeleccionado) {
      showError('Debe seleccionar un producto primero');
      return;
    }

    // Verificar si la cuenta ya está asociada
    const cuentaExistente = cuentasAsociadas.find(
      (c) => c.codigo === codigoCuentaNueva.trim() && c.tipo === tipoAsociacion
    );
    if (cuentaExistente) {
      showError('Esta cuenta ya está asociada con este tipo');
      return;
    }

    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_asociarcuenta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tablaInvStr,
          producto_id: productoSeleccionado.id,
          codigo_cuenta: codigoCuentaNueva.trim(),
          tipo_asociacion: tipoAsociacion,
          usuario: usuarioLog,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Cuenta asociada exitosamente');
        setCodigoCuentaNueva('');
        setDescripcionCuentaNueva('');
        setBusquedaCuenta('');
        setCuentasSugeridas([]);
        setDialogoAgregarCuenta(false);
        cargarCuentasAsociadas();
      } else {
        const errorData = await response.json();
        // Si el endpoint no existe, actualizar directamente en el producto
        if (errorData.Error && errorData.Error.includes('no encontrado')) {
          await actualizarCuentaEnProducto();
        } else {
          showError(errorData.Error || 'Error al asociar cuenta');
        }
      }
    } catch (error) {
      // Si falla, intentar actualizar directamente
      await actualizarCuentaEnProducto();
    }
  };

  const actualizarCuentaEnProducto = async () => {
    if (!productoSeleccionado || !tablaInvStr) return;

    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      
      // Preparar datos para actualizar
      const datosActualizacion = {
        id_empresa: selectedCompany,
        tabla_inventario: tablaInvStr,
        producto_id: productoSeleccionado.id,
        localidad: productoSeleccionado.localidad,
        productoserializado: productoSeleccionado.productoserializado === 'S',
        codigoserializado: productoSeleccionado.codigo,
        descripcion: productoSeleccionado.descripcion,
        tipoproducto: productoSeleccionado.tipoproducto,
        catproducto: productoSeleccionado.catproducto,
        unidadmedida: productoSeleccionado.medida,
        costord: productoSeleccionado.costo,
        mayor: productoSeleccionado.mayor,
        precio: productoSeleccionado.precio,
        existenciainicial: productoSeleccionado.inicial,
        cantcaja: productoSeleccionado.cantcaja,
        cantminima: productoSeleccionado.cantmin,
        cantmaxima: productoSeleccionado.cantmax,
        itbis: productoSeleccionado.itbis,
        cuentainventario: tipoAsociacion === 'Inventario' ? codigoCuentaNueva.trim() : productoSeleccionado.cuentainvent || '',
        cuentagasto: tipoAsociacion === 'Gasto' ? codigoCuentaNueva.trim() : productoSeleccionado.cuentagasto || '',
        observaciones: productoSeleccionado.garantia || '',
      };

      const { id_empresa: _ie, ...restActualizacion } = datosActualizacion;
      const response = await fetch(servidor + '/api/productosbackend_modificar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...restActualizacion,
        }),
      });

      if (response.ok) {
        showSuccess('Cuenta asociada exitosamente');
        setCodigoCuentaNueva('');
        setDescripcionCuentaNueva('');
        setBusquedaCuenta('');
        setCuentasSugeridas([]);
        setDialogoAgregarCuenta(false);
        // Recargar producto y cuentas
        await seleccionarProducto(productoSeleccionado.codigo);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al asociar cuenta');
      }
    } catch (error) {
      showError('Error de conexión al asociar cuenta');
    }
  };

  const handleEliminarCuenta = async (codigo, tipo) => {
    if (!puedeEliminar) {
      showError('No tiene permiso para eliminar asociaciones de cuentas.');
      return;
    }
    if (!productoSeleccionado) return;

    if (!window.confirm(`¿Está seguro que desea eliminar la asociación de la cuenta ${codigo}?`)) {
      return;
    }

    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_eliminarcuenta', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: tablaInvStr,
          producto_id: productoSeleccionado.id,
          codigo_cuenta: codigo,
          tipo_asociacion: tipo,
          usuario: usuarioLog,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Asociación eliminada exitosamente');
        cargarCuentasAsociadas();
      } else {
        const errorData = await response.json();
        // Si el endpoint no existe, actualizar directamente
        if (errorData.Error && errorData.Error.includes('no encontrado')) {
          await eliminarCuentaDeProducto(codigo, tipo);
        } else {
          showError(errorData.Error || 'Error al eliminar asociación');
        }
      }
    } catch (error) {
      await eliminarCuentaDeProducto(codigo, tipo);
    }
  };

  const eliminarCuentaDeProducto = async (codigo, tipo) => {
    if (!productoSeleccionado || !tablaInvStr) return;

    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      
      const datosActualizacion = {
        id_empresa: selectedCompany,
        tabla_inventario: tablaInvStr,
        producto_id: productoSeleccionado.id,
        localidad: productoSeleccionado.localidad,
        productoserializado: productoSeleccionado.productoserializado === 'S',
        codigoserializado: productoSeleccionado.codigo,
        descripcion: productoSeleccionado.descripcion,
        tipoproducto: productoSeleccionado.tipoproducto,
        catproducto: productoSeleccionado.catproducto,
        unidadmedida: productoSeleccionado.medida,
        costord: productoSeleccionado.costo,
        mayor: productoSeleccionado.mayor,
        precio: productoSeleccionado.precio,
        existenciainicial: productoSeleccionado.inicial,
        cantcaja: productoSeleccionado.cantcaja,
        cantminima: productoSeleccionado.cantmin,
        cantmaxima: productoSeleccionado.cantmax,
        itbis: productoSeleccionado.itbis,
        cuentainventario: tipo === 'Inventario' ? '' : productoSeleccionado.cuentainvent || '',
        cuentagasto: tipo === 'Gasto' ? '' : productoSeleccionado.cuentagasto || '',
        observaciones: productoSeleccionado.garantia || '',
      };

      const { id_empresa: _ie, ...restActualizacion } = datosActualizacion;
      const response = await fetch(servidor + '/api/productosbackend_modificar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          ...restActualizacion,
        }),
      });

      if (response.ok) {
        showSuccess('Asociación eliminada exitosamente');
        await seleccionarProducto(productoSeleccionado.codigo);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al eliminar asociación');
      }
    } catch (error) {
      showError('Error de conexión al eliminar asociación');
    }
  };

  const cerrarAgregarDialogo = () => {
    setDialogoAgregarCuenta(false);
    setCodigoCuentaNueva('');
    setDescripcionCuentaNueva('');
    setBusquedaCuenta('');
    setCuentasSugeridas([]);
  };

  const chipSxPorTipoCuenta = (tipo) => {
    if (tipo === 'Inventario') {
      return { bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue, fontWeight: 600 };
    }
    if (tipo === 'Gasto') {
      return { bgcolor: FP.color.infoSoft, color: FP.color.info, fontWeight: 600 };
    }
    return { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 600 };
  };

  if (!selectedCompany?.db_empresa) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Cuentas contables" subtitle="Seleccione una empresa activa" />
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 520,
              width: '100%',
              ...cardShellSx,
              p: { xs: 3, md: 4 },
              textAlign: 'center',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 48, color: FP.color.textMuted, mb: 2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 1 }}>
              Sin empresa seleccionada
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
              Elija una empresa en la barra superior para asociar cuentas contables a productos o servicios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Cuentas contables" subtitle="Permisos · Acceso restringido" showQuickActions={false} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Card
            elevation={0}
            sx={{
              maxWidth: 560,
              width: '100%',
              ...cardShellSx,
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso Denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            No tiene permiso de lectura del programa «cuentascontables» en roles (módulo Productos).
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
          </Typography>
        </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cuentas contables"
        subtitle={
          nombreNegocio
            ? `${nombreNegocio} · Asociación por producto o servicio`
            : 'Asociación por producto o servicio'
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de Búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600, display: { xs: 'none', lg: 'block' } }}>
              {[nombreNegocio, 'Producto · Inventario · Gasto · Otra'].filter(Boolean).join(' · ')}
            </Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Grid container spacing={2.5} alignItems="flex-start">
            <Grid item xs={12} sm={8}>
              <Autocomplete
                disablePortal
                options={productosBusqueda}
                getOptionLabel={(option) => option.texto || ''}
                inputValue={busquedaTexto}
                onInputChange={(e, newValue, reason) => {
                  setBusquedaTexto(newValue);
                  if (reason === 'input' && newValue && newValue.length > 2) {
                    realizarBusqueda(newValue);
                  }
                }}
                onChange={(e, newValue) => {
                  if (newValue) {
                    seleccionarProducto(newValue.codigo);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Buscar producto o servicio"
                    placeholder="Escriba para buscar…"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={INPUT_SX}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={() => setDialogoBusqueda(true)}
                sx={{ ...outlineBtnSx, height: 46 }}
              >
                Buscar en diálogo
              </Button>
            </Grid>
          </Grid>

          {productoSeleccionado && (
            <Box sx={{ mt: 2.5 }}>
              <Divider sx={{ mb: 2, borderColor: FP.color.border }} />
              <Typography sx={{ mb: 1.5, fontWeight: 700, fontSize: '0.92rem', color: FP.color.textPrimary }}>
                Producto seleccionado
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: FP.color.textSecondary, mb: 0.25 }}>
                    Código
                  </Typography>
                  <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary }}>{productoSeleccionado.codigo}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: FP.color.textSecondary, mb: 0.25 }}>
                    Descripción
                  </Typography>
                  <Typography sx={{ fontWeight: 600, color: FP.color.textPrimary }}>{productoSeleccionado.descripcion}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: FP.color.textSecondary, mb: 0.25 }}>
                    Tipo
                  </Typography>
                  <Typography sx={{ color: FP.color.textPrimary }}>{productoSeleccionado.tipoproducto || 'Producto'}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: FP.color.textSecondary, mb: 0.25 }}>
                    Categoría
                  </Typography>
                  <Typography sx={{ color: FP.color.textPrimary }}>{productoSeleccionado.catproducto || 'Materia Prima'}</Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </CardContent>
      </Card>

      {productoSeleccionado && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <StatCard
              label="Producto"
              value={String(productoSeleccionado.codigo || '—')}
              icon={DescriptionIcon}
              accent={FP.color.accentBlue}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <StatCard
              label="Cuentas asociadas"
              value={String(cuentasAsociadas.length)}
              icon={AccountBalanceIcon}
              accent={FP.color.navy}
            />
          </Grid>
        </Grid>
      )}

      {productoSeleccionado && (
        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.2,
                  bgcolor: FP.color.accentBlueSoft,
                  color: FP.color.accentBlue,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AccountBalanceIcon sx={{ fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Cuentas asociadas
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!puedeModificar}
              onClick={() => setDialogoAgregarCuenta(true)}
              sx={{ ...primaryBtnSx, px: 2.2, height: 38 }}
            >
              Agregar cuenta
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box display="flex" justifyContent="center" py={6}>
                <Typography sx={{ color: FP.color.textSecondary }}>Cargando cuentas…</Typography>
              </Box>
            ) : cuentasAsociadas.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <AccountBalanceIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay cuentas para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  {puedeModificar
                    ? 'Use «Agregar cuenta» para vincular inventario, gasto u otras cuentas.'
                    : 'No tiene permiso de modificación para agregar cuentas en esta pantalla.'}
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 2, border: `1px solid ${FP.color.border}`, overflow: 'auto' }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>Código</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>Descripción</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>Tipo</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: FP.color.textSecondary }}>Acciones</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cuentasAsociadas.map((cuenta, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{cuenta.codigo}</TableCell>
                        <TableCell>{cuenta.descripcion || cuenta.codigo}</TableCell>
                        <TableCell>
                          <Chip label={cuenta.tipo} size="small" sx={chipSxPorTipoCuenta(cuenta.tipo)} />
                        </TableCell>
                        <TableCell align="right">
                          {puedeEliminar && (
                            <IconButton
                              size="small"
                              onClick={() => handleEliminarCuenta(cuenta.codigo, cuenta.tipo)}
                              sx={{
                                color: FP.color.danger,
                                '&:hover': { bgcolor: FP.color.dangerSoft },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={dialogoBusqueda}
        onClose={() => setDialogoBusqueda(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flex: '1 1 auto', minWidth: 0 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }} component="span">
              Buscar producto o servicio
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDialogoBusqueda(false)}
            size="small"
            sx={{
              color: FP.color.textSecondary,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 1.2,
              '&:hover': { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
            }}
          >
            <ClearIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, overflow: 'visible' }}>
          <Autocomplete
            disablePortal
            options={productosBusqueda}
            getOptionLabel={(option) => option.texto || ''}
            inputValue={busquedaTexto}
            onInputChange={(e, newValue, reason) => {
              setBusquedaTexto(newValue);
              if (reason === 'input' && newValue && newValue.length > 2) {
                realizarBusqueda(newValue);
              }
            }}
            onChange={(e, newValue) => {
              if (newValue) {
                seleccionarProducto(newValue.codigo);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Buscar producto"
                placeholder="Escriba para buscar…"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={{ ...INPUT_SX, mt: 1 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button variant="outlined" onClick={() => setDialogoBusqueda(false)} sx={{ ...outlineBtnSx, px: 2, height: 38 }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogoAgregarCuenta}
        onClose={cerrarAgregarDialogo}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flex: '1 1 auto', minWidth: 0 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <AccountBalanceIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
              Agregar cuenta contable
            </Typography>
          </Box>
          <IconButton
            onClick={cerrarAgregarDialogo}
            size="small"
            sx={{
              color: FP.color.textSecondary,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 1.2,
              '&:hover': { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
            }}
          >
            <ClearIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 0, pt: 1 }}>
          <Grid container spacing={2.25} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth sx={INPUT_SX}>
                <InputLabel id="acc-tipo-asoc">Tipo de asociación</InputLabel>
                <Select
                  labelId="acc-tipo-asoc"
                  label="Tipo de asociación"
                  value={tipoAsociacion}
                  onChange={(e) => setTipoAsociacion(e.target.value)}
                >
                  <MenuItem value="Inventario">Inventario</MenuItem>
                  <MenuItem value="Gasto">Gasto</MenuItem>
                  <MenuItem value="Otra">Otra</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                disablePortal
                options={cuentasSugeridas}
                getOptionLabel={(option) => option.texto || option.codigo || ''}
                inputValue={busquedaCuenta}
                onInputChange={(e, newValue, reason) => {
                  setBusquedaCuenta(newValue);
                  if (reason === 'input') {
                    buscarCuentas(newValue);
                    setCodigoCuentaNueva(newValue);
                  }
                }}
                onChange={(e, newValue) => {
                  if (newValue) {
                    setCodigoCuentaNueva(newValue.codigo);
                    setDescripcionCuentaNueva(newValue.descripcion);
                    setBusquedaCuenta(newValue.texto || newValue.codigo);
                  } else {
                    setCodigoCuentaNueva('');
                    setDescripcionCuentaNueva('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Código de cuenta"
                    placeholder="Escriba código o nombre para buscar"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={INPUT_SX}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                value={descripcionCuentaNueva}
                disabled
                placeholder="La descripción se cargará automáticamente"
                sx={{
                  ...INPUT_SX,
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: FP.color.textSecondary,
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 2, gap: 1 }}>
          <Button variant="outlined" onClick={cerrarAgregarDialogo} sx={{ ...outlineBtnSx, px: 2, height: 38 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleAgregarCuenta}
            disabled={!codigoCuentaNueva.trim() || !puedeModificar}
            sx={primaryBtnSx}
          >
            Agregar
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default AsociacionCuentasContables;
